import type { AssistantEntry } from '@charitystack/dashboard-assistant-contracts';
import type { DashboardAssistantDockMessage } from '../../../../contexts/DashboardAssistantDockContext';
import type {
  AssistantRuntimeMessageLike,
  DashboardSearchResultPayload,
} from '../types';
import {
  parseDashboardCompactSearchToolResult,
  parseDashboardContextResult,
  parseDashboardDonationSummaryToolResult,
  parseDashboardNavigationResult,
  parseDashboardSearchResult,
  parseDashboardToolError,
} from './toolResultParsers';

const FRONTEND_ASSISTANT_FALLBACK_SOURCE = 'dashboard-assistant-frontend-assistant-fallback';
const TOOL_CALL_EMPTY_FALLBACK_TEXT = 'The assistant did not return a response';

// Only chat text counts. assistant-ui parts such as `{ type: 'reasoning', text }`
// also carry a string `text`; reading them leaks the model's reasoning into the
// bubble and stops the live reply from matching its persisted copy (prod test
// 2026-10-14, C3). A typed part counts only when its type is 'text'.
function getTextPartContent(part: unknown): string {
  if (typeof part === 'string') {
    return part;
  }

  if (!part || typeof part !== 'object') {
    return '';
  }

  const record = part as Record<string, unknown>;
  if (record.type !== undefined && record.type !== 'text') {
    return '';
  }
  if (typeof record.text === 'string') {
    return record.text;
  }
  if (typeof record.content === 'string') {
    return record.content;
  }

  return '';
}

export function getTextContent(content: unknown): string {
  if (Array.isArray(content)) {
    return content.map(getTextPartContent).filter(Boolean).join('\n');
  }

  return getTextPartContent(content);
}

const TOOL_CALL_ARTIFACT_PATTERN =
  /(?:<\/?an[-:]?tml|<\/?an_calls|(?:^|\b)tml:|function_calls?|<\/?invoke\b|<\/?parameter\b|parameter_:parameter|cFunction\b|<\/?z\b|_call>|<invoke|<\/invoke|<parameter)/i;
const TOOL_NAME_ARTIFACT_PATTERN =
  /\b(?:open_current|open_contact|open_payment_contact|open_subscription_contact|open_form|open_element|search_dashboard_records|get_current_dashboard_context|get_contact_donation_summary|query_dashboard_analytics)\b/i;
const STANDALONE_TOOL_FRAGMENT_PATTERN =
  /^(?:the:|date was|donation amount|cFunction|tml>|\/?>an_calls.*|\/?>invoke.*|[a-z_]*function[_\s-]*call[a-z_]*|[a-z_]*parameter[a-z_]*:?|[a-z_]*contact[a-z0-9_<>"'=\-/]*|[a-z_]*form[a-z0-9_<>"'=\-/]*)$/i;

function hasToolCallArtifact(content: string) {
  return TOOL_CALL_ARTIFACT_PATTERN.test(content) || TOOL_NAME_ARTIFACT_PATTERN.test(content);
}

export function sanitizeAssistantVisibleContent(content: string) {
  if (!content || !hasToolCallArtifact(content)) {
    return content;
  }

  const normalizedContent = content.replace(/\r\n?/g, '\n');
  const blocks = normalizedContent
    .split(/\n{2,}/)
    .filter((block) => !TOOL_CALL_ARTIFACT_PATTERN.test(block));

  const cleanedLines = blocks
    .join('\n\n')
    .split('\n')
    .filter((line) => {
      const trimmed = line.trim();
      if (!trimmed) {
        return true;
      }
      if (TOOL_CALL_ARTIFACT_PATTERN.test(trimmed)) {
        return false;
      }
      if (TOOL_NAME_ARTIFACT_PATTERN.test(trimmed)) {
        return false;
      }
      if (STANDALONE_TOOL_FRAGMENT_PATTERN.test(trimmed)) {
        return false;
      }
      return true;
    })
    .join('\n');

  return cleanedLines
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function getAssistantActionType(content: string): DashboardAssistantDockMessage['actionType'] | null {
  if (parseDashboardSearchResult(content) || parseDashboardCompactSearchToolResult(content)) {
    return 'search';
  }
  if (parseDashboardNavigationResult(content)) {
    return 'navigation';
  }
  if (parseDashboardContextResult(content)) {
    return 'context';
  }
  if (parseDashboardToolError(content)) {
    return 'error';
  }
  if (parseDashboardDonationSummaryToolResult(content)) {
    return 'search';
  }

  return null;
}

export function getMessagesForAgentContext(messages: DashboardAssistantDockMessage[] = []) {
  return messages
    .filter((message) => message.kind !== 'action')
    .map(({ actionType: _actionType, feedback: _feedback, kind: _kind, metadata: _metadata, ...message }) => ({
      ...message,
      id: message.id || getDockMessageKey(message),
    }));
}

export function getAgentMessageSyncKey(messages: AssistantRuntimeMessageLike[] | DashboardAssistantDockMessage[] = []) {
  return JSON.stringify(
    messages.map((message) => ({
      content: getTextContent(message.content),
      id: message.id || null,
      role: message.role === 'user' ? 'user' : 'assistant',
    })),
  );
}

export function getDockMessageSyncKey(messages: DashboardAssistantDockMessage[] = []) {
  return JSON.stringify(
    messages.map((message) => ({
      actionType: message.actionType || null,
      content: message.content,
      feedback: message.feedback || null,
      id: message.id || null,
      kind: message.kind,
      parentEntryId: message.parentEntryId || null,
      role: message.role,
      runId: message.runId || null,
      sequence: message.sequence || null,
      status: message.status || null,
      timestamp: message.timestamp || null,
      turnId: message.turnId || null,
    })),
  );
}

export function getDockMessageKey(message: DashboardAssistantDockMessage) {
  return message.id || `${message.role}:${message.content.trim()}`;
}

export function getDockMessageSyncKeys(message: DashboardAssistantDockMessage) {
  return uniqueSyncKeys(message.id, `${message.role}:${message.content.trim()}`);
}

function asString(value: unknown) {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

export { asString };

export function normalizeMessageTurnOrder(messages: DashboardAssistantDockMessage[] = []) {
  if (messages.some((message) => typeof message.sequence === 'number')) {
    return sortMessagesByCanonicalOrder(messages);
  }

  const normalizedMessages: DashboardAssistantDockMessage[] = [];
  let pendingPlainMessages: DashboardAssistantDockMessage[] = [];

  const flushPendingPlainMessages = () => {
    if (pendingPlainMessages.length === 0) {
      return;
    }

    const firstAssistantIndex = pendingPlainMessages.findIndex(
      (message) => message.role === 'assistant',
    );
    const hasGroupedUserThenAssistantMessages =
      firstAssistantIndex > 1 &&
      pendingPlainMessages.slice(0, firstAssistantIndex).every((message) => message.role === 'user') &&
      pendingPlainMessages.slice(firstAssistantIndex).every((message) => message.role === 'assistant');

    if (!hasGroupedUserThenAssistantMessages) {
      normalizedMessages.push(...pendingPlainMessages);
      pendingPlainMessages = [];
      return;
    }

    const userMessages = pendingPlainMessages.slice(0, firstAssistantIndex);
    const assistantMessages = pendingPlainMessages.slice(firstAssistantIndex);
    const pairedCount = Math.min(userMessages.length, assistantMessages.length);

    for (let index = 0; index < pairedCount; index += 1) {
      normalizedMessages.push(userMessages[index], assistantMessages[index]);
    }
    normalizedMessages.push(...userMessages.slice(pairedCount), ...assistantMessages.slice(pairedCount));
    pendingPlainMessages = [];
  };

  messages.forEach((message) => {
    if (message.kind === 'action' || message.displayPayload) {
      flushPendingPlainMessages();
      normalizedMessages.push(message);
      return;
    }

    pendingPlainMessages.push(message);
  });

  flushPendingPlainMessages();
  return normalizedMessages;
}

export function sortMessagesByCanonicalOrder(messages: DashboardAssistantDockMessage[] = []) {
  return [...messages].sort((left, right) => {
    const leftHasSequence = typeof left.sequence === 'number' && Number.isFinite(left.sequence);
    const rightHasSequence = typeof right.sequence === 'number' && Number.isFinite(right.sequence);

    if (leftHasSequence && rightHasSequence && left.sequence !== right.sequence) {
      return (left.sequence ?? 0) - (right.sequence ?? 0);
    }

    if (leftHasSequence && !rightHasSequence) {
      return -1;
    }

    if (!leftHasSequence && rightHasSequence) {
      return 1;
    }

    const leftTimestamp = left.timestamp || '';
    const rightTimestamp = right.timestamp || '';
    const timestampComparison = leftTimestamp.localeCompare(rightTimestamp);
    if (timestampComparison !== 0) {
      return timestampComparison;
    }

    return (left.id || '').localeCompare(right.id || '');
  });
}

export function getRuntimeMessageKey(message: AssistantRuntimeMessageLike) {
  return message.id || `${message.role === 'user' ? 'user' : 'assistant'}:${getTextContent(message.content).trim()}`;
}

export function getRuntimeMessageSyncKeys(message: AssistantRuntimeMessageLike) {
  return uniqueSyncKeys(
    message.id,
    `${message.role === 'user' ? 'user' : 'assistant'}:${getTextContent(message.content).trim()}`,
  );
}

function uniqueSyncKeys(...keys: Array<string | undefined>) {
  return [...new Set(keys.filter((key): key is string => Boolean(key)))];
}

export function toDockMessages(messages: AssistantRuntimeMessageLike[] = []): DashboardAssistantDockMessage[] {
  return messages
    .map<DashboardAssistantDockMessage | null>((message) => {
      if (message.isTextMessage && !message.isTextMessage()) {
        return null;
      }
      const content = getTextContent(message.content);
      if (!content.trim()) {
        return null;
      }

      const role = message.role === 'user' ? 'user' : 'assistant';
      const visibleContent =
        role === 'assistant' ? sanitizeAssistantVisibleContent(content) : content;
      if (!visibleContent.trim()) {
        return null;
      }

      const actionType = role === 'assistant' ? getAssistantActionType(visibleContent) : null;
      const timestamp =
        typeof message.timestamp === 'string'
          ? message.timestamp
          : typeof message.createdAt === 'string'
            ? message.createdAt
            : message.createdAt instanceof Date
              ? message.createdAt.toISOString()
              : null;

      return {
        id:
          message.id ||
          `${role}-${timestamp || 'message'}-${visibleContent.trim().slice(0, 80)}`,
        actionType: actionType || undefined,
        content: visibleContent,
        kind: actionType ? 'action' : 'message',
        role,
        timestamp,
      } satisfies DashboardAssistantDockMessage;
    })
    .filter((message): message is DashboardAssistantDockMessage => message !== null);
}

export function assistantEntryToDockMessage(entry: AssistantEntry): DashboardAssistantDockMessage {
  const role = entry.role === 'user' ? 'user' : 'assistant';
  const content =
    role === 'assistant' && entry.kind === 'message'
      ? sanitizeAssistantVisibleContent(entry.content)
      : entry.content;

  return {
    id: entry.entryId,
    actionType:
      entry.kind === 'action' || entry.kind === 'error'
        ? ((entry.metadata?.actionType as DashboardAssistantDockMessage['actionType']) ||
            (entry.kind === 'error' ? 'error' : 'tool'))
        : undefined,
    content,
    feedback:
      entry.metadata && typeof entry.metadata.feedback === 'string'
        ? (entry.metadata.feedback as 'up' | 'down')
        : ((entry as AssistantEntry & { feedback?: 'up' | 'down' }).feedback ?? null),
    kind: entry.kind === 'action' || entry.kind === 'error' ? 'action' : 'message',
    role,
    timestamp: entry.createdAt,
    sequence: entry.sequence,
    status: entry.status,
    turnId: entry.turnId,
    runId: entry.runId,
    parentEntryId: entry.parentEntryId,
    displayPayload: entry.displayPayload,
    metadata: entry.metadata,
  };
}

export function cleanAssistantHistoryMessages(
  messages: DashboardAssistantDockMessage[] = [],
) {
  const backendAssistantTextKeys = new Set(
    messages
      .filter(
        (message) =>
          message.role === 'assistant' &&
          message.kind !== 'action' &&
          getMetadataString(message, 'source') !== FRONTEND_ASSISTANT_FALLBACK_SOURCE,
      )
      .map(getAssistantTextKey),
  );
  const seenFallbackKeys = new Set<string>();

  return messages.filter((message) => {
    if (isToolCallOnlyEmptyFallback(message)) {
      return false;
    }

    if (getMetadataString(message, 'source') !== FRONTEND_ASSISTANT_FALLBACK_SOURCE) {
      return true;
    }

    const key = getAssistantTextKey(message);
    if (backendAssistantTextKeys.has(key)) {
      return false;
    }
    if (seenFallbackKeys.has(key)) {
      return false;
    }

    seenFallbackKeys.add(key);
    return true;
  });
}

export function getMessageDedupeKey(message: DashboardAssistantDockMessage) {
  if (message.id) {
    return `id:${message.id}`;
  }

  const searchResult = parseDashboardSearchResult(message.content);
  if (searchResult?.query) {
    return `action:dashboard_search_results:${searchResult.query.toLowerCase()}`;
  }

  const compactSearchResult = parseDashboardCompactSearchToolResult(message.content);
  if (compactSearchResult?.query) {
    return `action:dashboard_search_results:${compactSearchResult.query.toLowerCase()}`;
  }

  if (
    message.displayPayload?.renderer === 'dashboard_search_results' &&
    message.displayPayload.data &&
    typeof (message.displayPayload.data as DashboardSearchResultPayload).query === 'string'
  ) {
    return `action:dashboard_search_results:${(message.displayPayload.data as DashboardSearchResultPayload).query?.toLowerCase()}`;
  }

  if (
    message.displayPayload?.renderer === 'dashboard_navigation' &&
    typeof message.displayPayload.data?.path === 'string'
  ) {
    return `action:dashboard_navigation:${message.displayPayload.data.path}`;
  }

  return message.id || `${message.role}:${message.content.trim()}`;
}

export function mergeDockMessages(
  historyMessages: DashboardAssistantDockMessage[] = [],
  liveMessages: DashboardAssistantDockMessage[] = [],
) {
  const seen = new Set<string>();
  const mergedMessages: DashboardAssistantDockMessage[] = [];

  [...historyMessages, ...liveMessages].forEach((message) => {
    const key = getMessageDedupeKey(message);
    if (seen.has(key)) {
      return;
    }

    seen.add(key);
    mergedMessages.push(message);
  });

  return mergedMessages;
}

function getMessageShadowKey(message: DashboardAssistantDockMessage) {
  const displayPayload = message.displayPayload;
  if (displayPayload) {
    return [
      'display',
      message.actionType || '',
      displayPayload.renderer,
      displayPayload.summary || '',
      stableStringify(displayPayload.data || {}),
    ].join(':');
  }

  const parsedActivity =
    parseDashboardSearchResult(message.content) ||
    parseDashboardCompactSearchToolResult(message.content) ||
    parseDashboardNavigationResult(message.content) ||
    parseDashboardContextResult(message.content) ||
    parseDashboardToolError(message.content) ||
    parseDashboardDonationSummaryToolResult(message.content);

  if (parsedActivity) {
    return `activity:${message.actionType || ''}:${stableStringify(parsedActivity)}`;
  }

  return `${message.kind || 'message'}:${message.role}:${message.content.trim()}`;
}

function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value);
  }

  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }

  const record = value as Record<string, unknown>;
  return `{${Object.keys(record)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${stableStringify(record[key])}`)
    .join(',')}}`;
}

export function mergePersistedHistoryWithLiveMessages(
  historyMessages: DashboardAssistantDockMessage[] = [],
  liveMessages: DashboardAssistantDockMessage[] = [],
) {
  const historyHasCanonicalOrder = historyMessages.some(
    (message) => typeof message.sequence === 'number',
  );
  if (!historyHasCanonicalOrder) {
    return mergeDockMessages(historyMessages, liveMessages);
  }

  const historyIds = new Set(historyMessages.map((message) => message.id).filter(Boolean));
  const historyShadowKeys = new Set(historyMessages.map(getMessageShadowKey));
  const shadowedLiveMessages = liveMessages.filter((message) => {
    if (message.id && historyIds.has(message.id)) {
      return false;
    }

    return !historyShadowKeys.has(getMessageShadowKey(message));
  });

  return mergeDockMessages(historyMessages, shadowedLiveMessages);
}

function countTextMessagesByRole(messages: DashboardAssistantDockMessage[] = []) {
  return messages.reduce(
    (counts, message) => {
      if (message.kind === 'action' || message.displayPayload) {
        return counts;
      }

      if (message.role === 'user') {
        counts.user += 1;
      } else {
        counts.assistant += 1;
      }

      return counts;
    },
    { assistant: 0, user: 0 },
  );
}

export function isSessionHistoryRegression(
  currentMessages: DashboardAssistantDockMessage[] = [],
  nextMessages: DashboardAssistantDockMessage[] = [],
) {
  if (currentMessages.length === 0 || nextMessages.length === 0) {
    return false;
  }

  const currentCounts = countTextMessagesByRole(currentMessages);
  const nextCounts = countTextMessagesByRole(nextMessages);
  const currentAssistantKeys = new Set(
    currentMessages
      .filter((message) => message.role === 'assistant' && message.kind !== 'action')
      .map(getDockMessageKey),
  );
  const nextAssistantKeys = new Set(
    nextMessages
      .filter((message) => message.role === 'assistant' && message.kind !== 'action')
      .map(getDockMessageKey),
  );
  const lostAssistantMessages = [...currentAssistantKeys].some(
    (messageKey) => !nextAssistantKeys.has(messageKey),
  );

  return (
    nextCounts.assistant < currentCounts.assistant ||
    (lostAssistantMessages && nextCounts.user <= currentCounts.user)
  );
}

export function getFrontendToolEntryRole(message: DashboardAssistantDockMessage) {
  return message.actionType === 'error' ? 'error' : 'action';
}

export function getFrontendToolEntryKind(message: DashboardAssistantDockMessage) {
  return message.actionType === 'error' ? 'error' : 'action';
}

function getMetadataString(message: DashboardAssistantDockMessage, key: string) {
  const value = message.metadata?.[key];
  return typeof value === 'string' ? value : undefined;
}

function getAssistantTextKey(message: DashboardAssistantDockMessage) {
  return `${message.role}:${message.content.trim().replace(/\s+/g, ' ')}`;
}

function isToolCallOnlyEmptyFallback(message: DashboardAssistantDockMessage) {
  return (
    message.role === 'assistant' &&
    message.kind !== 'action' &&
    message.content.includes(TOOL_CALL_EMPTY_FALLBACK_TEXT) &&
    getMetadataString(message, 'finishReason') === 'tool-calls' &&
    message.metadata?.emptyText === true
  );
}
